import { LoadableStatus } from '@aiwyn/shared';
import { Comment } from '../../client-requests/components/comments/comments.model';
import { CrlActivityEntry, CrlItemStatus, CrlRequestDetail } from '../models';

/**
 * A patch applied to a single request item on top of the loaded `detailData`,
 * merged in selector code (cxp-state "merge logic in selectors") and reconciled
 * away when the real detail reloads.
 *
 * Despite the name, a status-moving action patches on the server's ANSWER rather
 * than ahead of it: the card holds as it is until the call returns, then takes
 * exactly what was sent, so the client is never shown a state the server has not
 * agreed to. The reload that follows replaces the patch with server truth.
 */
export interface CrlItemOptimisticPatch {
  status?: CrlItemStatus;
  statusLabel?: string;
  isNotApplicable?: boolean;
  notApplicableReason?: string | null;
  /**
   * Patched alongside `status` by "I'm Finished" so the Undo link appears as
   * soon as the client submits, rather than only once the detail reload lands.
   * See `CrlRequestItem.isAwaitingReview` for why Undo is keyed on this rather
   * than on the display status.
   */
  isAwaitingReview?: boolean;
}

export interface CxpCrlDetailStateModel {
  // 1. Navigation IDs (synced from route-params service + legacy selection bridge)
  selectedRequestId: number | null;
  /** Item shown in the panel — the `:itemId` of the named `panel` outlet child. */
  selectedItemId: number | null;

  // 2. Core data — owns ONLY the selected request's detail; task-list data stays
  // in CxpTasksState and is blended at selector time, never duplicated here.
  detailStatus: LoadableStatus;
  detailData: CrlRequestDetail | null;

  // 3. Optimistic patches — keyed by item id; merged into `detailData` items in
  // the selectors so the panel reflects "I'm Finished" / "Mark N/A" instantly.
  optimisticItemPatches: Record<number, CrlItemOptimisticPatch>;

  /**
   * Items with an undo-finished call in flight. Undo is deliberately NOT
   * optimistic (product flow: wait for the BE, then flip back to the editable
   * state via the reload), so the card only disables the Undo link while the
   * item id sits in this list.
   */
  pendingUndoItemIds: number[];

  // 4. Per-item activity feed — cached by item id so returning to an item shows
  // the previously-loaded entries immediately while a fresh load runs
  // (stale-while-revalidate). `itemActivityLoadingItemId` is the item whose feed
  // is currently being (re)fetched, driving the loading indicator.
  itemActivityByItem: Record<number, CrlActivityEntry[]>;
  itemActivityLoadingItemId: number | null;

  // 5. Per-item comments thread — cached by item id with the same
  // stale-while-revalidate behaviour as the activity feed above.
  // `itemCommentsLoadingItemId` is the item whose thread is being (re)fetched.
  itemCommentsByItem: Record<number, Comment[]>;
  itemCommentsLoadingItemId: number | null;

  /**
   * Item whose lazy `/item/detail` (files + answer) fetch is in flight. Parallels
   * `itemActivityLoadingItemId`; the files/answer themselves patch onto the item
   * inside `detailData`, this just tracks the pending load.
   */
  itemDetailLoadingItemId: number | null;

  /**
   * Items whose last answer save was refused and has not been retried since.
   * "I'm Finished" reads this before submitting, so the firm is never asked to
   * review an answer the server never stored. Cleared when a save is retried.
   */
  answerSaveFailedItemIds: number[];
}

export const DEFAULT_CXP_CRL_DETAIL_STATE: CxpCrlDetailStateModel = {
  selectedRequestId: null,
  selectedItemId: null,
  detailStatus: LoadableStatus.DEFAULT,
  detailData: null,
  optimisticItemPatches: {},
  pendingUndoItemIds: [],
  itemActivityByItem: {},
  itemActivityLoadingItemId: null,
  itemCommentsByItem: {},
  itemCommentsLoadingItemId: null,
  itemDetailLoadingItemId: null,
  answerSaveFailedItemIds: [],
};
